import React, { useState, useEffect, useCallback } from 'react';
import { getAllStudentsActivitySummary } from '../services/activityService';
import EmailReportModal from './EmailReportModal';
import Toast from './Toast';
import './AnalyticsDashboard.css';

/* ── Accuracy badge helper ── */
const getAccuracyBadge = (pct) => {
  if (pct === '—') return { label: '—', tier: 'none' };
  const n = parseInt(pct, 10);
  if (n >= 80) return { label: pct, tier: 'high' };
  if (n >= 50) return { label: pct, tier: 'medium' };
  return { label: pct, tier: 'low' };
};

/* ── CSV builder (also used by EmailReportModal) ── */
function buildCsvString(summaries) {
  const header = 'Student,Attempts,Correct,Accuracy (%),Avg Voice Score (%),Daily Living Scenarios Completed,Last Active';
  const rows = summaries.map((s) => {
    const accuracy = s.total === 0 ? '—' : Math.round((s.correct / s.total) * 100);
    const voice    = s.avgVoiceScore != null ? s.avgVoiceScore : '—';
    const last     = s.lastActive ?? 'No activity';
    return `"${s.name}",${s.total},${s.correct},${accuracy},${voice},${s.scenariosCompleted || 0},"${last}"`;
  });
  return [header, ...rows].join('\n');
}

const AnalyticsDashboard = () => {
  const [summaries, setSummaries]           = useState([]);
  const [isLoading, setIsLoading]           = useState(true);
  const [loadError, setLoadError]           = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [toast, setToast]                   = useState(null);

  const fetchSummaries = useCallback(() => {
    setIsLoading(true);
    setLoadError('');
    getAllStudentsActivitySummary().then(({ data, error }) => {
      if (error) {
        setLoadError(`Failed to load analytics: ${error.message || 'Unknown error'}. Check your Supabase connection and schema.`);
        setSummaries([]);
      } else {
        setSummaries(data || []);
      }
      setIsLoading(false);
    });
  }, []);

  useEffect(() => { fetchSummaries(); }, [fetchSummaries]);

  const totalAttempts = summaries.reduce((sum, s) => sum + s.total, 0);
  const totalCorrect  = summaries.reduce((sum, s) => sum + s.correct, 0);
  const overallPct    = totalAttempts === 0 ? 0 : Math.round((totalCorrect / totalAttempts) * 100);
  const activeCount   = summaries.filter((s) => s.total > 0).length;

  /* CSV download */
  const handleExportCsv = () => {
    const csv  = buildCsvString(summaries);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setToast({ message: 'CSV downloaded!', type: 'success' });
  };

  const noData = summaries.length === 0;

  return (
    <div>
      <h1 className="analytics-title">Reports &amp; Analytics</h1>

      {/* ── Action toolbar ── */}
      <div className="analytics-toolbar">
        <button
          className="analytics-btn btn-export"
          onClick={handleExportCsv}
          disabled={noData}
          title={noData ? 'No student data to export' : 'Download CSV report'}
        >
          <i className="fas fa-file-csv" aria-hidden="true"></i> Export CSV
        </button>
        <button
          className="analytics-btn btn-email"
          onClick={() => setShowEmailModal(true)}
          disabled={noData}
          title={noData ? 'No student data to email' : 'Email report to recipients'}
        >
          <i className="fas fa-envelope" aria-hidden="true"></i> Email Report
        </button>
        <button
          className="analytics-btn"
          onClick={fetchSummaries}
          disabled={isLoading}
          title="Refresh analytics data"
          style={{ marginLeft: 'auto' }}
        >
          <i className={`fas fa-sync-alt${isLoading ? ' fa-spin' : ''}`} aria-hidden="true"></i> Refresh
        </button>
      </div>

      {noData && !isLoading && !loadError && (
        <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
          <i className="fas fa-info-circle" aria-hidden="true"></i>{' '}
          Export and Email buttons are enabled once student activity data is available.
        </p>
      )}

      {/* ── Error state ── */}
      {loadError && (
        <div style={{
          background: '#fdecea',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          padding: '1rem',
          color: '#721c24',
          marginBottom: '1rem',
        }} role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i>{' '}
          {loadError}
        </div>
      )}

      {/* ── Stats Grid ── */}
      <div className="analytics-stats-grid">
        <div className="analytics-stat-card">
          <h3>Total Students</h3>
          <p className="analytics-stat-value">{summaries.length}</p>
          <p className="analytics-stat-label">Registered</p>
        </div>
        <div className="analytics-stat-card">
          <h3>Students Active</h3>
          <p className="analytics-stat-value">{activeCount}</p>
          <p className="analytics-stat-label positive">Have activity</p>
        </div>
        <div className="analytics-stat-card">
          <h3>Total Attempts</h3>
          <p className="analytics-stat-value">{totalAttempts}</p>
          <p className="analytics-stat-label">Across all students</p>
        </div>
        <div className="analytics-stat-card">
          <h3>Overall Accuracy</h3>
          <p className="analytics-stat-value">{overallPct}%</p>
          <p className="analytics-stat-label">{totalCorrect} correct</p>
        </div>
      </div>

      {/* ── Student Activity Table ── */}
      <h2 className="analytics-section-title">Student Activity</h2>

      {isLoading ? (
        <p className="analytics-loading">
          <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Loading…
        </p>
      ) : (
        <div className="analytics-table-wrapper">
          <table className="analytics-table" aria-label="Student activity summary">
            <thead>
              <tr>
                <th scope="col">Student</th>
                <th scope="col">Attempts</th>
                <th scope="col">Correct</th>
                <th scope="col">Accuracy</th>
                <th scope="col">Avg Voice 🎤</th>
                <th scope="col">Life Skills</th>
                <th scope="col">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {summaries.map((s) => {
                const pct   = s.total === 0 ? '—' : `${Math.round((s.correct / s.total) * 100)}%`;
                const badge = getAccuracyBadge(pct);
                const voice = s.avgVoiceScore != null ? `${s.avgVoiceScore}%` : '—';
                return (
                  <tr key={s.student_id}>
                    <td>{s.name}</td>
                    <td>{s.total}</td>
                    <td>{s.correct}</td>
                    <td>
                      <span className={`accuracy-badge ${badge.tier}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td>
                      <span className={`voice-badge ${s.avgVoiceScore != null ? (s.avgVoiceScore >= 60 ? 'pass' : 'low') : 'none'}`}>
                        {voice}
                      </span>
                    </td>
                    <td>
                      <i className="fas fa-hands-wash" aria-hidden="true"></i> {s.scenariosCompleted || 0}
                    </td>
                    <td>{s.lastActive ?? 'No activity yet'}</td>
                  </tr>
                );
              })}
              {summaries.length === 0 && !loadError && (
                <tr>
                  <td colSpan={7} className="analytics-empty">No students found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Email modal */}
      {showEmailModal && (
        <EmailReportModal
          summaries={summaries}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
};

export default AnalyticsDashboard;
